import type { PrismaEnums } from '@airreading/schema/browser';
import { OrgFeedbackType, type UserRoleWithSchool } from './data/index.js';

export const Role = {
  parent: 'parent',
  coach: 'coach',
  student: 'student',
  org_admin: 'org_admin',
  school_admin: 'school_admin',
  school_contact: 'school_contact',
  air_admin: 'air_admin',
  air_limited_admin: 'air_limited_admin',
  edge_coach: 'edge_coach',
} as const satisfies Record<PrismaEnums.Role, PrismaEnums.Role>;
export type Role = (typeof Role)[keyof typeof Role];

export const UserRoleStatus = {
  active: 'active',
  inactive: 'inactive',
} as const satisfies Record<PrismaEnums.UserRoleStatus, PrismaEnums.UserRoleStatus>;
export type UserRoleStatus = (typeof UserRoleStatus)[keyof typeof UserRoleStatus];

/**
 * Used for role checking only. ANY is not a role stored in the database
 */
const ANY = '*' as const;

export const ImpersonatingAsHeader = 'x-impersonating-as';

/**
 * AuthRole is a stripped down version of UserRole.
 * It only contains the role and schoolId which is enough to identify perform role/auth checks.
 */
export type AuthRole = {
  role: Role;
  schoolId: string | null;
  status: UserRoleStatus;
};

/**
 * Used for role checking only. ANY is not a role stored in the database
 */
export type AuthRoleWithAny = {
  role: Role | typeof ANY;
  schoolId: string | null | typeof ANY;
  status: UserRoleStatus;
};

export type WithAuthRole =
  | {
      user_roles: AuthRole[];
    }
  | {
      userRoles: AuthRole[];
    };

function userRolesOf(object: WithAuthRole): AuthRole[] {
  return 'user_roles' in object ? object.user_roles : object.userRoles;
}

export const isCoach = (x: WithAuthRole) =>
  userRolesOf(x).some(({ role, status }) => role === 'coach' && status === UserRoleStatus.active);
export const isCoachAllStatuses = (x: WithAuthRole) => userRolesOf(x).some(({ role }) => role === 'coach');
export const isCoachRole = ({ role }: AuthRole) => role === 'coach';
export const isStudent = (x: WithAuthRole) =>
  userRolesOf(x).some(({ role, status }) => role === 'student' && status === UserRoleStatus.active);
export const isStudentRole = ({ role }: AuthRole) => role === 'student';
/**
 * Student enrolled in an org running the family program (`Org.feedbackType = parentFeedback`).
 * Only the login payload loads roles with their school, so this is false elsewhere.
 */
export const isFamilyStudent = ({ userRoles }: { userRoles: UserRoleWithSchool[] }) =>
  userRoles.some(
    ({ role, status, school }) =>
      role === 'student' &&
      status === UserRoleStatus.active &&
      school?.orgFeedbackType === OrgFeedbackType.parentFeedback,
  );
/** True when the user has an active `air_admin` or `air_limited_admin` role. Inactive admin roles are ignored. */
export const isAirAdmin = (x: WithAuthRole) =>
  userRolesOf(x).some(
    ({ role, status }) =>
      (role === 'air_admin' || role === 'air_limited_admin') && status === UserRoleStatus.active,
  );
export const isAirAdminRole = ({ role }: AuthRole) => role === 'air_admin' || role === 'air_limited_admin';
export const isAirLimitedAdmin = (x: WithAuthRole) =>
  userRolesOf(x).some(({ role, status }) => role === 'air_limited_admin' && status === UserRoleStatus.active);

export const getNonAdminRoles = <T extends AuthRole>(roles: readonly T[]): T[] =>
  roles.filter((role) => !isAirAdminRole(role));

export const isAdminOnlyUser = (x: WithAuthRole): boolean =>
  isAirAdmin(x) && getNonAdminRoles(userRolesOf(x)).length === 0;

/**
 * Whether the user holds the full (and active)`air_admin` role. Unlike
 * {@link isAirAdmin} which returns true for both `air_admin` and
 * `air_limited_admin`.
 * TODO, these should probably be renamed as a no-op PR.
 */
export const isFullAirAdmin = (x: WithAuthRole) =>
  userRolesOf(x).some(({ role, status }) => role === 'air_admin' && status === UserRoleStatus.active);

/**
 * Whether the user's PII (e.g. `User.lastName`) must be masked on read and
 * stripped on write. True for an `air_limited_admin` who is NOT also a full
 * `air_admin`
 */
export const isPiiRestricted = (x: WithAuthRole) =>
  userRolesOf(x).some(({ role }) => role === 'air_limited_admin') && !isFullAirAdmin(x);
export const isSchoolAdmin = (x: WithAuthRole) =>
  userRolesOf(x).some(({ role, status }) => role === 'school_admin' && status === UserRoleStatus.active);
export const isSchoolAdminRole = ({ role }: AuthRole) => role === 'school_admin';
export const isSchoolContact = (x: WithAuthRole) => userRolesOf(x).some(({ role }) => role === 'school_contact');
export const isSchoolContactRole = ({ role }: AuthRole) => role === 'school_contact';

export const isEdgeCoach = (x: WithAuthRole) =>
  userRolesOf(x).some(({ role, status }) => role === 'edge_coach' && status === UserRoleStatus.active);
export const isEdgeCoachRole = ({ role }: AuthRole) => role === 'edge_coach';

export const isStudentOfSchool = (x: WithAuthRole, schoolIdToCheck: string) => {
  return userRolesOf(x).some(({ role, schoolId }) => role === 'student' && schoolId === schoolIdToCheck);
};
export const getSchoolIdOfStudent = (x: WithAuthRole): string | null | undefined => {
  return userRolesOf(x).find(isStudentRole)?.schoolId;
};

export const airAdmin = (): AuthRole => ({ role: 'air_admin', schoolId: null, status: 'active' });
export const coach = (): AuthRole => ({ role: 'coach', schoolId: null, status: 'active' });
export const student = (): AuthRole => ({ role: 'student', schoolId: ANY, status: 'active' });
export const studentOfSchool = (schoolId: string): AuthRole => ({ role: 'student', schoolId, status: 'active' });
export const schoolAdmin = (): AuthRole => ({ role: 'school_admin', schoolId: ANY, status: 'active' });
export const schoolAdminOfSchool = (schoolId: string): AuthRole => ({
  role: 'school_admin',
  schoolId,
  status: 'active',
});
export const schoolContactOfSchool = (schoolId: string): AuthRole => ({
  role: 'school_contact',
  schoolId,
  status: 'active',
});

export const getSchoolIdsOfSchoolAdmin = (x: WithAuthRole): string[] => {
  return userRolesOf(x)
    .filter(({ role, status, schoolId }) => role === 'school_admin' && status === UserRoleStatus.active && !!schoolId)
    .map(({ schoolId }) => schoolId!);
};

export const equalRoles = (role1: AuthRole | AuthRoleWithAny, role2: AuthRole | AuthRoleWithAny) => {
  const roleMatch = role1.role === ANY || role2.role === ANY || role1.role === role2.role;
  const schoolMatch = role1.schoolId === ANY || role2.schoolId === ANY || role1.schoolId === role2.schoolId;
  const statusMatch = role1.status === role2.status;

  return roleMatch && schoolMatch && statusMatch;
};
/**
 * Access-control role aliases: a user holding the key role implicitly satisfies
 * a requirement for any of the listed roles. For AUTHORIZATION MATCHING ONLY
 * (see {@link roleSatisfiesRequirement} / `RoleAuth`). Do NOT use this for role
 * data mutation (sync, status changes) — there the roles must stay distinct.
 *
 * An `air_limited_admin` gets the same route access as an `air_admin`, on every
 * HTTP method — the alias is not read-only. Its narrower data exposure is
 * enforced separately rather than by blocking writes: PII is masked on read and
 * stripped from write bodies (see `apps/server/src/auth/pii.ts`, wired in
 * `RoleAuth` and `index.ts`), and the escalation-sensitive paths are denied
 * explicitly — impersonation in `apps/server/src/auth/middleware.ts`, admin-role
 * changes in `apps/server/src/auth/role-changes.ts`. Both key off
 * {@link isFullAirAdmin}, not this alias.
 */
const ACCESS_CONTROL_ROLE_ALIASES: Partial<Record<Role, readonly Role[]>> = {
  air_limited_admin: ['air_admin'],
};

/**
 * Whether a user's role satisfies a required role for authorization. Honors
 * {@link equalRoles} and the access aliases above. The alias check compares on a
 * copy, so the caller's real roles are never rewritten.
 */
export const roleSatisfiesRequirement = (userRole: AuthRole, required: AuthRole | AuthRoleWithAny): boolean => {
  if (equalRoles(userRole, required)) {
    return true;
  }
  const aliasRoles = ACCESS_CONTROL_ROLE_ALIASES[userRole.role];
  return !!aliasRoles?.some((aliasRole) => equalRoles({ ...userRole, role: aliasRole }, required));
};

export const roleHash = (role: AuthRole) => `${role.role}:${role.schoolId}:${role.status}`;
export const deactivate = (role: AuthRole): AuthRole => ({ ...role, status: 'inactive' });
export const activateRole = (role: AuthRole): AuthRole => ({ ...role, status: 'active' });

export type SpecialRole = 'air_admin' | 'air_limited_admin' | 'coach' | 'edge_coach';
export const SPECIAL_ROLES: readonly Role[] = Object.freeze(['air_admin', 'air_limited_admin', 'coach', 'edge_coach']);

/**
 * Determines if the given role is a special role, i.e. it has no `schoolId` associated with it.
 */
export function isSpecialRole(role: Role): role is SpecialRole {
  return SPECIAL_ROLES.includes(role);
}

export function partitionRoles(roles?: Role[]): [SpecialRole[], Role[]] {
  if (!roles) {
    return [[], []];
  }

  const specialRoles: SpecialRole[] = [];
  const regularRoles: Role[] = [];
  for (const role of roles) {
    if (isSpecialRole(role)) {
      specialRoles.push(role);
    } else {
      regularRoles.push(role);
    }
  }

  return [specialRoles, regularRoles];
}

/**
 * Returns fullNameOrEmail with " (Edge tutors)" appended only when the user is not a coach or student.
 * Call only when the user is an Edge coach: isEdgeCoach ? coachFullNameWithEdgeTutors(name, user) : name
 */
export function coachFullNameWithEdgeTutors(fullNameOrEmail: string, user: WithAuthRole | null | undefined): string {
  return `${fullNameOrEmail}${user && isAirAdmin(user) ? ' (Edge tutors)' : ''}`;
}
